import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { openPropertyGuestGuide } from '@/events/propertyGuestGuide';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const propertyPagePaths = [
    '/the-belvedere',
    '/hannahs-pearl',
    '/sea-dreams',
    '/beach-beach-baby',
    '/bella-vita',
    '/sunset-bungalow-30a',
  ];
  const isPropertyPage = propertyPagePaths.includes(location.pathname);
  const navTextShadow = '0 1px 6px rgba(0,0,0,0.7), 0 1px 1px rgba(0,0,0,0.55)';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nav = isHome
    ? [
        { label: 'Stays', href: '#featured' },
        { label: 'Insider', href: '#concierge' },
        { label: 'Experiences', href: '#experiences' },
        { label: 'Join Our Rental Program', href: '/join-our-rental-program' },
        { label: 'Contact', href: '#contact' },
      ]
    : [
        { label: 'All Properties', href: '/vacation-search-results' },
        { label: 'Insider', href: '/#concierge' },
        { label: 'Experiences', href: '/#experiences' },
        { label: 'Join Our Rental Program', href: '/join-our-rental-program' },
        { label: 'Contact', href: '/#contact' },
      ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      {!scrolled && (
        <div
          className="absolute inset-x-0 top-0 h-28 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(13,17,20,0.56) 0%, rgba(13,17,20,0.24) 58%, rgba(13,17,20,0) 100%)',
          }}
        />
      )}
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src={scrolled ? 'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/ad0496bd-895d-4699-ad7f-e1eb49751466_compressed_inspire-CLASSY-WEB1.webp' : 'https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/a9dda6ae-3ab1-4d64-b562-0b538ac1d564_compressed_inspire-CLASSY-WHITEOUT-WEB1.webp'}
            alt="Inspire Vacation Properties"
            className="h-12 sm:h-14 2xl:h-[72px] w-auto"
            draggable={false}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-7 2xl:gap-9">
          {nav.map((n) => (
            isPropertyPage && n.label === 'Insider' ? (
              <button
                key={n.href}
                type="button"
                onClick={openPropertyGuestGuide}
                className={`text-[15px] font-medium cursor-pointer transition-colors ${
                  scrolled
                    ? 'text-foreground-850 hover:text-foreground-950'
                    : 'text-background-50 hover:text-background-100'
                }`}
                style={scrolled ? undefined : { textShadow: navTextShadow }}
              >
                {n.label}
              </button>
            ) : (
              <a
                key={n.href}
                href={n.href}
                className={`text-[15px] font-medium cursor-pointer transition-colors ${
                  scrolled
                    ? 'text-foreground-850 hover:text-foreground-950'
                    : 'text-background-50 hover:text-background-100'
                }`}
                style={scrolled ? undefined : { textShadow: navTextShadow }}
              >
                {n.label}
              </a>
            )
          ))}
        </nav>

        <div className="ml-auto xl:ml-0 flex items-center gap-3">
          {!isPropertyPage ? (
          <a
            href={isHome ? '#concierge' : '/#concierge'}
            className={`hidden sm:inline-flex text-sm cursor-pointer whitespace-nowrap px-4 py-2 rounded-lg border transition-all ${
              scrolled
                ? 'border-[#DED0BE] bg-[#EFE4D6] text-foreground-950 hover:bg-[#E9DCCB]'
                : 'border-[#DED0BE] bg-[#EFE4D6]/90 text-foreground-950 shadow-[0_10px_24px_rgba(0,0,0,0.16)] backdrop-blur-[2px] hover:bg-[#E9DCCB]'
            }`}
          >
            Ask the Insider
          </a>
          ) : (
          <button
            type="button"
            onClick={openPropertyGuestGuide}
            className={`hidden sm:inline-flex text-sm cursor-pointer whitespace-nowrap px-4 py-2 rounded-lg border transition-all ${
              scrolled
                ? 'border-[#DED0BE] bg-[#EFE4D6] text-foreground-950 hover:bg-[#E9DCCB]'
                : 'border-[#DED0BE] bg-[#EFE4D6]/90 text-foreground-950 shadow-[0_10px_24px_rgba(0,0,0,0.16)] backdrop-blur-[2px] hover:bg-[#E9DCCB]'
            }`}
          >
            Ask the Insider
          </button>
          )}
        </div>

        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          className={`xl:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer ${
            scrolled ? 'text-foreground-900' : 'text-background-50'
          }`}
          style={scrolled ? undefined : { textShadow: navTextShadow }}
        >
          <i className="ri-menu-line text-xl" />
        </button>
      </div>

      {mobileOpen && (
        <div className="xl:hidden fixed inset-0 z-[70] bg-background-50 animate-fade-up overflow-y-auto">
          <div className="h-24 px-6 flex items-center justify-between border-b border-background-200/70">
            <img
              src="https://storage.helloreaddy.io/project_files/6010ef82-6c94-407b-9fac-7f3ca6010bf2/ad0496bd-895d-4699-ad7f-e1eb49751466_compressed_inspire-CLASSY-WEB1.webp"
              alt="Inspire Vacation Properties"
              className="h-14 w-auto"
              draggable={false}
            />
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer text-foreground-900"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <div className="px-6 pt-6 pb-10 flex flex-col">
            {nav.map((n) => (
              isPropertyPage && n.label === 'Insider' ? (
                <button
                  key={n.href}
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openPropertyGuestGuide();
                  }}
                  className="text-left text-base font-medium text-foreground-950 py-4 border-b border-background-200/70 cursor-pointer"
                >
                  {n.label}
                </button>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-base font-medium text-foreground-950 py-4 border-b border-background-200/70 cursor-pointer"
                >
                  {n.label}
                </a>
              )
            ))}
            {!isPropertyPage ? (
            <a
              href={isHome ? '#concierge' : '/#concierge'}
              onClick={() => setMobileOpen(false)}
              className="mt-6 inline-flex items-center justify-center px-5 py-4 rounded-lg bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              Ask the Insider
              <i className="ri-arrow-right-up-line ml-2" />
            </a>
            ) : (
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                openPropertyGuestGuide();
              }}
              className="mt-6 inline-flex items-center justify-center px-5 py-4 rounded-lg bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              Ask the Insider
              <i className="ri-arrow-right-up-line ml-2" />
            </button>
            )}
            <p className="mt-8 text-xs uppercase tracking-[0.22em] text-foreground-500">
              Panama City Beach · 30A · Rosemary Beach · WaterSound
            </p>
          </div>
        </div>
      )}
    </header>
  );
}
